import * as R from 'ramda'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { DateString } from '@weblite/ts-action'
import { Story, StoryPeerType, StoryView } from '@weblite/ts-action/story'
import {
  serverRibbonStoriesView,
  storiesStorage,
  storiesView,
  storiesViewByPeerId,
  StoryStorageState,
  uploadingStoriesView,
} from '$logic/story/storage'
import {
  StoryRibbon,
  StoryRibbonPriority,
  UploadingStory,
} from '$logic/story/types'
import { useClientUser } from '$logic/client-user'
import { CAN_CREATE_STORY_STORY_COUNT } from '$comp/_app/components/story/constants'
import useBrowserOnline from '$hooks/use-browser-online'
import { useUser } from '$logic/users'
import { useHall } from '$logic/hall/react-hook'
import { isClientsRoom } from '$logic/room'
import { usePvByUserId } from '$logic/pv'
import { getStories, getViewersList } from './service'
import {
  getNonSubscribedRibbon,
  useIsLoadingNonSubscribedRibbons,
} from './non-subscribed-ribbons'
import { sortByDate } from '$func/sort'
import useEffectWithPrevDeps from '$hooks/use-effect-with-prev-deps'

export function useStories() {
  return useSelector(storiesView) as Story[]
}

export function useStoriesByPeerIdAndPeerType(
  peerId: string,
  peerType: StoryPeerType,
): Story[] | undefined {
  return useSelector(storiesViewByPeerId(peerId, peerType))
}

export function getStoriesByPeerIdAndPeerType(
  peerId: string,
  peerType: StoryPeerType,
): Story[] | undefined {
  return storiesStorage.getStoriesByPeerIdAndPeerType(peerId, peerType)
}

export function useUploadingStories():
  | Record<string, UploadingStory>
  | undefined {
  return useSelector(uploadingStoriesView)
}

// TODO[review-story]: First apply map then uniq function
export function useUploadingStoriesPeers() {
  const uploadingStoriesStorage = useUploadingStories()
  return useMemo(
    () =>
      R.uniq(Object.values(uploadingStoriesStorage || {})).map(
        uploadingStory => ({
          peerId: uploadingStory.peerId,
          peerType: uploadingStory.peerType,
          lastStoryAt: uploadingStory.uploadStartedAt,
        }),
      ),
    [uploadingStoriesStorage],
  )
}

export function useUploadingStoriesPeersByClientAndNonClient() {
  const clientUserId = useClientUser().id
  const storiesPeers = useUploadingStoriesPeers()

  const nonClientPeers = storiesPeers.filter(
    ({ peerId, peerType }) => !(peerId === clientUserId && peerType === 'USER'),
  )

  const clientPeer = storiesPeers.find(
    ({ peerId, peerType }) => peerId === clientUserId && peerType === 'USER',
  )
  return { client: clientPeer, nonClientPeers }
}

export function useUploadingStoriesByPeerIdAndPeerType(
  peerId: string,
  peerType: StoryPeerType,
) {
  const uploadingStoriesStorage = useUploadingStories()
  return useMemo(() => {
    return Object.values(uploadingStoriesStorage || {})?.find(
      uploadingStory =>
        uploadingStory.peerId === peerId &&
        uploadingStory.peerType === peerType,
    )
  }, [uploadingStoriesStorage, peerId, peerType])
}

export function useSubscribedServerStoryRibbons() {
  const ribbons = useSelector(serverRibbonStoriesView) as StoryRibbon[]

  return useMemo(() => ribbons.filter(ribbon => !ribbon.nonSubscribed), [
    ribbons,
  ])
}

export function useStoryRibbonByPeerIdAndPeerType(
  peerId: string,
  peerType: StoryPeerType,
) {
  const ribbons = useSelector(serverRibbonStoriesView) as StoryRibbon[]

  return ribbons.find(
    ribbon => ribbon.peerId === peerId && ribbon.peerType === peerType,
  )
}

export function useSelectedServerStoryRibbonIndex(
  peerId: string,
  peerType: StoryPeerType,
) {
  const storyRibbons = useSubscribedServerStoryRibbons()
  return storyRibbons?.findIndex(storyRibbon => {
    return storyRibbon.peerId === peerId && storyRibbon.peerType === peerType
  })
}

export function useServerStoryRibbonsWithSeparatedClientUserStory() {
  const storyRibbons = useSubscribedServerStoryRibbons()
  const clientUserId = useClientUser()?.id

  const clientUserRibbonStory = useMemo(
    () =>
      storyRibbons.find(
        story => story.peerId === clientUserId && story.peerType === 'USER',
      ),
    [storyRibbons, clientUserId],
  )

  const nonClientRibbonStories = useMemo(
    () =>
      storyRibbons.filter(
        story => !(story.peerId === clientUserId && story.peerType === 'USER'),
      ),
    [storyRibbons, clientUserId],
  )

  return {
    nonClientRibbonStories,
    clientUserRibbonStory,
  }
}

export function useStoryRibbons() {
  const {
    clientUserRibbonStory,
    nonClientRibbonStories,
  } = useServerStoryRibbonsWithSeparatedClientUserStory()

  const {
    client: clientUpload,
    nonClientPeers: nonClientUploads,
  } = useUploadingStoriesPeersByClientAndNonClient()

  const groupedNonClientPeersByUploadStatus = useMemo(() => {
    if (!nonClientRibbonStories?.length)
      return {
        OTHERS: [],
        JUST_UPLOADED: [],
        UPLOADING: [],
      }

    return R.reduce(
      (acc, ribbon) => {
        const ribbonHasUploadingStory =
          nonClientUploads.findIndex(
            ncu =>
              ncu.peerId === ribbon.peerId && ncu.peerType === ribbon.peerType,
          ) !== -1

        return ribbonHasUploadingStory
          ? { ...acc, UPLOADING: [...acc.UPLOADING, ribbon] }
          : ribbon.priority === StoryRibbonPriority.UPLOADING
          ? { ...acc, JUST_UPLOADED: [...acc.JUST_UPLOADED, ribbon] }
          : { ...acc, OTHERS: [...acc.OTHERS, ribbon] }
      },
      { UPLOADING: [], JUST_UPLOADED: [], OTHERS: [] },
      nonClientRibbonStories,
    )
  }, [nonClientRibbonStories, nonClientUploads])

  return useMemo(() => {
    const clientUserRibbonStoryItem =
      clientUserRibbonStory ||
      (clientUpload
        ? ({
            ...clientUpload,
            stories: [],
            lastStoryAt: new Date(),
            priority: StoryRibbonPriority.INFINITY,
          } as StoryRibbon)
        : undefined)

    const nonClientRibbonStoryItems = [
      ...sortByDate(
        [
          ...nonClientUploads.map(ncu => {
            const peerStories =
              nonClientRibbonStories.find(
                ribbon =>
                  ribbon.peerId === ncu.peerId &&
                  ribbon.peerType === ncu.peerType,
              )?.stories || []

            return {
              ...ncu,
              stories: peerStories,
              priority: StoryRibbonPriority.UPLOADING,
            } as StoryRibbon
          }),
          ...groupedNonClientPeersByUploadStatus.JUST_UPLOADED,
        ],
        ribbon => ribbon.lastStoryAt,
        'descending',
      ),
      ...groupedNonClientPeersByUploadStatus.OTHERS,
    ]

    const ribbons = clientUserRibbonStoryItem
      ? [clientUserRibbonStoryItem].concat(nonClientRibbonStoryItems)
      : nonClientRibbonStoryItems

    return {
      clientUserRibbon: clientUserRibbonStoryItem,
      nonClientRibbons: nonClientRibbonStoryItems,
      allRibbons: ribbons,
    }
  }, [
    clientUserRibbonStory,
    clientUpload,
    nonClientUploads,
    nonClientRibbonStories,
    groupedNonClientPeersByUploadStatus,
  ])
}

export function useSelectedRibbonStory(
  peerId: string,
  peerType: StoryPeerType,
) {
  const { allRibbons } = useStoryRibbons()
  return useMemo(() => {
    return allRibbons.findIndex(
      storyList =>
        storyList.peerId === peerId && storyList.peerType === peerType,
    )
  }, [allRibbons, peerType, peerId])
}

export function useAvatarStories(
  peerId: string,
  peerType: StoryPeerType,
  isAvatarWithStory?: boolean,
  canClientUserCreateStory?: boolean,
): {
  storyCount: number
  seenStoryCount: number
  uploading?: boolean
  showCreateStoryBadge?: boolean
  peerStoryRibbon?: StoryRibbon
} {
  if (!isAvatarWithStory) return { storyCount: 0, seenStoryCount: 0 }

  const peerStoryRibbon = useStoryRibbonByPeerIdAndPeerType(peerId, peerType)

  const uploadingStory = useUploadingStoriesByPeerIdAndPeerType(
    peerId,
    peerType,
  )

  const peerStories = peerStoryRibbon?.stories || []

  const storyCount = peerStories.length + (uploadingStory ? 1 : 0)
  const seenStoryCount = peerStories.filter(({ viewedAt }) => Boolean(viewedAt))
    .length

  const seenAllStories = storyCount === seenStoryCount

  if (canClientUserCreateStory && storyCount === 0) {
    return {
      storyCount: CAN_CREATE_STORY_STORY_COUNT,
      seenStoryCount: 0,
      showCreateStoryBadge: true,
    }
  }

  return {
    storyCount: Math.min(storyCount, 1),
    seenStoryCount: seenAllStories ? 1 : 0,
    uploading: !!uploadingStory,
    peerStoryRibbon,
  }
}

export function useUserAvatarStories(
  peerId: string,
  isAvatarWithStory?: boolean,
  canClientUserCreateStory?: boolean,
): {
  storyCount: number
  seenStoryCount: number
  showCreateStoryBadge?: boolean
  uploading?: boolean
} {
  const peerType = 'USER'
  const { user } = useUser(peerId)

  const isContact = user?.isContact
  const { pv, loading: loadingPv, error: pvError } = usePvByUserId(peerId)
  const userHavePv = isClientsRoom(pv?.roomId || '')

  const {
    storyCount,
    seenStoryCount,
    uploading,
    peerStoryRibbon,
    showCreateStoryBadge,
  } = useAvatarStories(
    peerId,
    peerType,
    isAvatarWithStory,
    canClientUserCreateStory,
  )

  const isLoadingNonSubscribedRibbons = useIsLoadingNonSubscribedRibbons()

  useEffect(() => {
    if (
      !isContact &&
      !peerStoryRibbon &&
      !isLoadingNonSubscribedRibbons &&
      !loadingPv &&
      !pvError &&
      !userHavePv &&
      user &&
      isAvatarWithStory &&
      !canClientUserCreateStory
    ) {
      getNonSubscribedRibbon({ peerId, peerType })
    }
  }, [
    userHavePv,
    isContact,
    user,
    peerStoryRibbon,
    isLoadingNonSubscribedRibbons,
    canClientUserCreateStory,
    isAvatarWithStory,
    peerId,
    peerType,
    loadingPv,
    pvError,
  ])

  return {
    storyCount,
    seenStoryCount,
    uploading,
    showCreateStoryBadge, // TODO:
  }
}

export function useHallAvatarStories(
  peerId: string,
  peerType: StoryPeerType,
  isAvatarWithStory?: boolean,
  canClientUserCreateStory?: boolean,
): {
  storyCount: number
  seenStoryCount: number
  showCreateStoryBadge?: boolean
  uploading?: boolean
} {
  const { entity: hall } = useHall(peerId)
  const isPublic = hall?.public
  const subscribed = isClientsRoom(peerId)

  const {
    storyCount,
    seenStoryCount,
    uploading,
    peerStoryRibbon,
    showCreateStoryBadge,
  } = useAvatarStories(
    peerId,
    peerType,
    isAvatarWithStory,
    canClientUserCreateStory,
  )

  const isLoadingNonSubscribedRibbons = useIsLoadingNonSubscribedRibbons()

  useEffect(() => {
    if (
      hall &&
      !subscribed &&
      !peerStoryRibbon &&
      isPublic &&
      !isLoadingNonSubscribedRibbons &&
      isAvatarWithStory
    ) {
      getNonSubscribedRibbon({ peerId, peerType })
    }
  }, [
    isPublic,
    subscribed,
    peerStoryRibbon,
    isLoadingNonSubscribedRibbons,
    peerType,
    peerId,
    isAvatarWithStory,
    hall,
  ])

  return { storyCount, seenStoryCount, uploading, showCreateStoryBadge }
}

export function usePeerStories({
  storyRibbon,
  isSlideActive,
  onLoadStories,
}: {
  storyRibbon: StoryRibbon
  isSlideActive: boolean
  onLoadStories: () => void
}) {
  const { peerId, peerType } = storyRibbon
  const stories = useStoriesByPeerIdAndPeerType(peerId, peerType)
  const isOnline = useBrowserOnline()
  const [isLoading, setIsLoading] = useState(false)
  const [hasError, setHasError] = useState(false)

  const fetchStories = useCallback(
    async (storyRibbon: StoryRibbon, retryCount: number = 0) => {
      setIsLoading(true)
      setHasError(false)
      try {
        await getStories([storyRibbon])
        setIsLoading(false)
        onLoadStories()
      } catch (err) {
        if (retryCount < 3 && isOnline)
          fetchStories(storyRibbon, retryCount + 1)
        else setHasError(true)
      }
    },
    [isOnline, onLoadStories],
  )

  const retry = useCallback(() => {
    fetchStories(storyRibbon)
  }, [storyRibbon, fetchStories])

  useEffectWithPrevDeps(() => {
    if (
      !isLoading &&
      storyRibbon.peerId &&
      storyRibbon.peerType &&
      stories === undefined &&
      isSlideActive &&
      storyRibbon.stories.length
    ) {
      if (!isOnline) setHasError(true)
      // NOTE: THIS ONLY FETCHES ONCE
      else fetchStories(storyRibbon)
    }
  }, [storyRibbon, stories, isSlideActive, isLoading, fetchStories, isOnline])

  return { stories, isLoading, hasError, retry }
}

export function useGetViewersList(
  peerId: string,
  peerType: StoryPeerType,
  storyId?: string,
) {
  const [before, setBefore] = useState<DateString>(new Date().toString())
  const [isDone, setIsDone] = useState<boolean>(false)
  const [views, setViews] = useState<StoryView[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const attachViews = useCallback((newViews: StoryView[]) => {
    setViews(oldViews => [...oldViews, ...newViews])
  }, [])

  const reset = () => {
    setBefore(new Date().toString())
    setIsDone(false)
    setViews([])
  }

  const loadMore = async () => {
    if (isLoading || !storyId) return
    try {
      setIsLoading(true)
      const { views, done } = await getViewersList(
        peerId,
        peerType,
        storyId,
        before,
      )
      attachViews(views)
      if (views.length) setBefore(views[views.length - 1].viewedAt)
      if (done) setIsDone(true)
      setIsLoading(false)
    } catch (e) {
      setIsLoading(false)
    }
  }

  return {
    isLoading,
    isDone,
    views,
    loadMore,
    reset,
  }
}
